import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuilding, faMoneyBillWave, faSave, faTimes, faSpinner,
    faCheckCircle, faExclamationTriangle, faPhone, faEnvelope,
    faMapMarkerAlt, faWallet, faHistory, faSearch, faFilter, faFileInvoice,
    faCalendarAlt, faCreditCard, faUniversity, faEye,
    faReceipt, faInfoCircle, faArrowLeft, faArrowRight,
    faUser, faTag, faUsers, faPlus, faUpload, faFileImage,
    faDownload, faTrash, faExchangeAlt, faArrowUp, faArrowDown,
    faPrint, faFilePdf, faFileExcel, faUndo, faMoneyBill, faShoppingCart,
    faBalanceScale, faList, faPlusCircle, faRupeeSign, faBarcode,
    faLandmark, faIdCard, faCalendarWeek, faCalendarDay, faChevronLeft,
    faChevronRight, faSort, faSortUp, faSortDown, faEdit
} from '@fortawesome/free-solid-svg-icons';
import "../styles/CustomerAccount.css";

// ==============================================
// Helper Functions
// ==============================================
const formatCurrency = (amount) => {
    return `Rs. ${parseFloat(amount || 0).toFixed(2)}`;
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN');
};

// ==============================================
// Toast Notification Component
// ==============================================
const CaToastNotification = ({ toast, setToast, translations }) => {
    if (!toast.show) return null;
    return (
        <div className={`ca-toast-notification ${toast.type}`}>
            <div className="ca-toast-content">
                <div className="ca-toast-icon">
                    <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faExclamationTriangle} />
                </div>
                <div className="ca-toast-text">
                    <div className="ca-toast-message">{toast.message}</div>
                </div>
                <button className="ca-toast-close" onClick={() => setToast({ show: false })}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// Sortable Table Header Component
// ==============================================
const CaSortableHeader = ({ column, label, sortConfig, onSort, translations }) => {
    const isActive = sortConfig.key === column;
    return (
        <th onClick={() => onSort(column)} className="ca-sortable-header">
            {label}
            <span className="ca-sort-indicator">
                {isActive ? (
                    sortConfig.direction === 'asc' ?
                        <FontAwesomeIcon icon={faSortUp} /> :
                        <FontAwesomeIcon icon={faSortDown} />
                ) : (
                    <FontAwesomeIcon icon={faSort} />
                )}
            </span>
        </th>
    );
};

// ==============================================
// Receipt Preview Modal
// ==============================================
const CaReceiptPreviewModal = ({ show, onClose, receiptUrl, receiptName }) => {
    if (!show) return null;

    const fullUrl = receiptUrl ? `https://ourzestapp.online/oz_backend/${receiptUrl}` : null;

    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-receipt-preview-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faFileImage} /> Receipt Preview</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="ca-modal-body">
                    {receiptName && <p className="ca-receipt-filename">{receiptName}</p>}
                    {fullUrl ? (
                        fullUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                            <img src={fullUrl} alt="Receipt" className="ca-receipt-image" />
                        ) : (
                            <div className="ca-receipt-file-link">
                                <FontAwesomeIcon icon={faFileImage} size="4x" />
                                <a href={fullUrl} target="_blank" rel="noopener noreferrer">View Receipt File</a>
                            </div>
                        )
                    ) : (
                        <p>No receipt available</p>
                    )}
                </div>
                <div className="ca-modal-footer">
                    {fullUrl && (
                        <a href={fullUrl} download className="ca-btn-primary" style={{ textDecoration: 'none' }}>
                            <FontAwesomeIcon icon={faDownload} /> Download
                        </a>
                    )}
                    <button className="ca-btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Transaction Details Modal
// ==============================================
const CaTransactionDetailsModal = ({ show, onClose, transaction, onViewReceipt }) => {
    if (!show || !transaction) return null;

    const getTransactionDisplayType = () => {
        if (transaction.transaction_type === 'debit') {
            return 'Payment';
        } else {
            if (transaction.source === 'purchase') {
                return 'Purchase';
            } else {
                return 'Return';
            }
        }
    };

    const getTransactionIcon = () => {
        if (transaction.transaction_type === 'debit') {
            return faArrowDown;
        } else {
            if (transaction.source === 'purchase') {
                return faShoppingCart;
            } else {
                return faArrowUp;
            }
        }
    };

    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-details-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faReceipt} /> Transaction Details</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="ca-modal-body">
                    <div className="ca-details-grid">
                        <div className="ca-detail-row">
                            <span className="ca-detail-label">Transaction No:</span>
                            <span className="ca-detail-value">{transaction.transaction_no}</span>
                        </div>
                        <div className="ca-detail-row">
                            <span className="ca-detail-label">Date:</span>
                            <span className="ca-detail-value">{formatDate(transaction.transaction_date)}</span>
                        </div>
                        <div className="ca-detail-row">
                            <span className="ca-detail-label">Type:</span>
                            <span className={`ca-detail-value ca-type-${transaction.transaction_type}`}>
                                <FontAwesomeIcon icon={getTransactionIcon()} style={{ marginRight: '8px' }} />
                                {getTransactionDisplayType()}
                                <small style={{ display: 'block', fontSize: '10px', color: '#666', marginTop: '4px' }}>
                                    {transaction.transaction_type === 'debit' ? 'Decreases what you owe' : 'Increases what you owe'}
                                </small>
                            </span>
                        </div>
                        <div className="ca-detail-row">
                            <span className="ca-detail-label">Source:</span>
                            <span className="ca-detail-value">
                                {transaction.source === 'purchase' ? 'Purchase Order' :
                                    transaction.source === 'manual' ? 'Manual Entry' : 'Other'}
                            </span>
                        </div>
                        <div className="ca-detail-row">
                            <span className="ca-detail-label">Reference:</span>
                            <span className="ca-detail-value">{transaction.reference_no || '-'}</span>
                        </div>
                        <div className="ca-detail-row">
                            <span className="ca-detail-label">Amount:</span>
                            <span className={`ca-detail-value ca-amount ${transaction.transaction_type}`}>
                                {transaction.transaction_type === 'debit' ? '-' : '+'}{formatCurrency(transaction.amount)}
                            </span>
                        </div>
                        <div className="ca-detail-row">
                            <span className="ca-detail-label">Payment Method:</span>
                            <span className="ca-detail-value">{transaction.payment_method?.replace('_', ' ') || 'Cash'}</span>
                        </div>
                        <div className="ca-detail-row">
                            <span className="ca-detail-label">Balance Before:</span>
                            <span className="ca-detail-value">{formatCurrency(transaction.balance_before)}</span>
                        </div>
                        <div className="ca-detail-row">
                            <span className="ca-detail-label">Balance After:</span>
                            <span className="ca-detail-value">{formatCurrency(transaction.balance_after)}</span>
                        </div>
                        {transaction.description && (
                            <div className="ca-detail-row">
                                <span className="ca-detail-label">Description:</span>
                                <span className="ca-detail-value">{transaction.description}</span>
                            </div>
                        )}
                        {transaction.notes && (
                            <div className="ca-detail-row">
                                <span className="ca-detail-label">Notes:</span>
                                <span className="ca-detail-value">{transaction.notes}</span>
                            </div>
                        )}
                        {transaction.receipt_image && (
                            <div className="ca-detail-row">
                                <span className="ca-detail-label">Receipt:</span>
                                <span className="ca-detail-value">
                                    <button
                                        className="ca-btn-link"
                                        onClick={() => onViewReceipt(transaction.receipt_image, transaction.receipt_original_name)}
                                    >
                                        <FontAwesomeIcon icon={faFileImage} /> View Receipt
                                    </button>
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="ca-modal-footer">
                    <button className="ca-btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Edit Supplier Transaction Modal Component
// ==============================================
const CaEditSupplierTransactionModal = ({ show, transaction, supplier, onClose, onSave, showToast, currentShopId }) => {
    const [amount, setAmount] = useState('');
    const [transactionDate, setTransactionDate] = useState('');
    const [description, setDescription] = useState('');
    const [referenceNo, setReferenceNo] = useState('');
    const [notes, setNotes] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const { user } = useAuth();

    const shopId = currentShopId || localStorage.getItem('current_shop_id') || '';

    useEffect(() => {
        if (show && transaction) {
            setAmount(transaction.amount?.toString() || '');
            setTransactionDate(transaction.transaction_date?.split('T')[0] || new Date().toISOString().split('T')[0]);
            setDescription(transaction.description || '');
            setReferenceNo(transaction.reference_no || '');
            setNotes(transaction.notes || '');
            setPaymentMethod(transaction.payment_method || 'cash');
            setErrors({});
        }
    }, [show, transaction]);

    const validateForm = () => {
        const newErrors = {};
        if (!amount || parseFloat(amount) <= 0) {
            newErrors.amount = 'Please enter a valid amount';
        }
        if (!transactionDate) {
            newErrors.transaction_date = 'Transaction date is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        if (saving) return;
        setSaving(true);

        try {
            const username = user?.username || localStorage.getItem('username');
            
            const payload = {
                action: 'update_transaction',
                transaction_id: transaction.id,
                supplier_id: supplier?.supplierid,
                shop_id: shopId,
                amount: parseFloat(amount),
                transaction_date: transactionDate,
                description: description,
                reference_no: referenceNo,
                notes: notes,
                payment_method: paymentMethod,
                username: username
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/po_supplier_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', 'Transaction updated successfully');
                if (onSave) {
                    await onSave();
                }
                onClose();
            } else {
                showToast('error', data.message || 'Failed to update transaction');
            }
        } catch (error) {
            console.error('Update error:', error);
            showToast('error', 'Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const paymentMethodsList = [
        { value: 'cash', label: 'Cash' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'digital_wallet', label: 'Digital Wallet' },
        { value: 'cheque', label: 'Cheque' },
        { value: 'card', label: 'Card' },
        { value: 'upi', label: 'UPI' }
    ];

    const isRTL = (localStorage.getItem('ourzest_language') || 'en') === 'ur' || (localStorage.getItem('ourzest_language') || 'en') === 'ar';

    if (!show) return null;

    const getTransactionDisplayType = () => {
        if (transaction?.transaction_type === 'debit') {
            return 'Payment';
        } else {
            if (transaction?.source === 'purchase') {
                return 'Purchase';
            } else {
                return 'Return';
            }
        }
    };

    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-edit-transaction-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faEdit} /> Edit Supplier Transaction</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="ca-modal-body">
                    {transaction && supplier && (
                        <div className="ca-customer-info-summary">
                            <div className="ca-customer-name-large">
                                <FontAwesomeIcon icon={faBuilding} />
                                {supplier.supplier_name}
                            </div>
                            <div className="ca-transaction-type-info">
                                <span className={`ca-transaction-type-badge ${transaction.transaction_type}`}>
                                    {getTransactionDisplayType()}
                                </span>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="ca-form-grid">
                            <div className={`ca-form-group ${errors.amount ? 'has-error' : ''}`}>
                                <div className="ca-input-container">
                                    <FontAwesomeIcon icon={faRupeeSign} className="ca-input-icon" />
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        placeholder=" "
                                        disabled={saving}
                                        dir="ltr"
                                    />
                                    <label className="ca-input-label">Amount <span className="ca-required">*</span></label>
                                </div>
                                {errors.amount && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.amount}</div>}
                            </div>

                            <div className={`ca-form-group ${errors.transaction_date ? 'has-error' : ''}`}>
                                <div className="ca-input-container">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="ca-input-icon" />
                                    <input
                                        type="date"
                                        value={transactionDate}
                                        onChange={(e) => setTransactionDate(e.target.value)}
                                        disabled={saving}
                                        dir="ltr"
                                    />
                                    <label className="ca-input-label">Transaction Date <span className="ca-required">*</span></label>
                                </div>
                                {errors.transaction_date && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.transaction_date}</div>}
                            </div>

                            <div className="ca-form-group">
                                <div className="ca-input-container">
                                    <FontAwesomeIcon icon={faBarcode} className="ca-input-icon" />
                                    <input
                                        type="text"
                                        value={referenceNo}
                                        onChange={(e) => setReferenceNo(e.target.value)}
                                        placeholder=" "
                                        disabled={saving}
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label className="ca-input-label">Reference Number</label>
                                </div>
                            </div>

                            <div className="ca-form-group">
                                <div className="ca-input-container">
                                    <FontAwesomeIcon icon={faCreditCard} className="ca-input-icon" />
                                    <select
                                        value={paymentMethod}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                        disabled={saving}
                                        className="ca-form-select"
                                    >
                                        {paymentMethodsList.map(method => (
                                            <option key={method.value} value={method.value}>
                                                {method.label}
                                            </option>
                                        ))}
                                    </select>
                                    <label className="ca-input-label">Payment Method</label>
                                </div>
                            </div>

                            <div className="ca-form-group ca-full-width">
                                <div className="ca-input-container">
                                    <FontAwesomeIcon icon={faFileInvoice} className="ca-input-icon" />
                                    <input
                                        type="text"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder=" "
                                        disabled={saving}
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label className="ca-input-label">Description</label>
                                </div>
                            </div>

                            <div className="ca-form-group ca-full-width">
                                <div className="ca-input-container ca-textarea-container">
                                    <FontAwesomeIcon icon={faReceipt} className="ca-input-icon" />
                                    <textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder=" "
                                        disabled={saving}
                                        rows="3"
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    ></textarea>
                                    <label className="ca-input-label">Notes</label>
                                </div>
                            </div>
                        </div>

                        <div className="ca-modal-footer">
                            <button type="button" className="ca-btn-secondary" onClick={onClose}>
                                Cancel
                            </button>
                            <button type="submit" className="ca-btn-primary" disabled={saving}>
                                {saving ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
                                ) : (
                                    <><FontAwesomeIcon icon={faSave} /> Save Changes</>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Delete Supplier Transaction Confirmation Modal
// ==============================================
const CaDeleteSupplierTransactionModal = ({ show, transaction, supplier, onClose, onConfirm, showToast, currentShopId }) => {
    const [deleting, setDeleting] = useState(false);
    const { user } = useAuth();
    const shopId = currentShopId || localStorage.getItem('current_shop_id') || '';

    const handleConfirm = async () => {
        if (deleting) return;
        setDeleting(true);

        try {
            const username = user?.username || localStorage.getItem('username');
            
            const payload = {
                action: 'delete_transaction',
                transaction_id: transaction.id,
                supplier_id: supplier?.supplierid,
                shop_id: shopId,
                username: username
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/po_supplier_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', 'Transaction deleted successfully');
                if (onConfirm) {
                    await onConfirm();
                }
                onClose();
            } else {
                showToast('error', data.message || 'Failed to delete transaction');
            }
        } catch (error) {
            console.error('Delete error:', error);
            showToast('error', 'Network error occurred');
        } finally {
            setDeleting(false);
        }
    };

    if (!show) return null;

    const getTransactionDisplayType = () => {
        if (transaction?.transaction_type === 'debit') {
            return 'Payment';
        } else {
            if (transaction?.source === 'purchase') {
                return 'Purchase';
            } else {
                return 'Return';
            }
        }
    };

    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-delete-confirm-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faTrash} /> Delete Transaction</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="ca-modal-body">
                    <div className="ca-delete-warning">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="ca-warning-icon" />
                        <p>Are you sure you want to delete this transaction?</p>
                        <p className="ca-warning-text">This action cannot be undone and will affect the supplier's balance.</p>
                    </div>
                    
                    {transaction && (
                        <div className="ca-transaction-details">
                            <div className="ca-detail-row">
                                <span>Transaction Type:</span>
                                <strong>{getTransactionDisplayType()}</strong>
                            </div>
                            <div className="ca-detail-row">
                                <span>Amount:</span>
                                <strong className={transaction.transaction_type === 'debit' ? 'ca-amount-debit' : 'ca-amount-credit'}>
                                    {transaction.transaction_type === 'debit' ? `- ` : `+ `}
                                    Rs. {parseFloat(transaction.amount || 0).toFixed(2)}
                                </strong>
                            </div>
                            <div className="ca-detail-row">
                                <span>Date:</span>
                                <strong>{new Date(transaction.transaction_date).toLocaleDateString()}</strong>
                            </div>
                            {transaction.reference_no && (
                                <div className="ca-detail-row">
                                    <span>Reference:</span>
                                    <strong>{transaction.reference_no}</strong>
                                </div>
                            )}
                            {transaction.description && (
                                <div className="ca-detail-row">
                                    <span>Description:</span>
                                    <strong>{transaction.description}</strong>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="ca-modal-footer">
                    <button type="button" className="ca-btn-secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="button" className="ca-btn-danger" onClick={handleConfirm} disabled={deleting}>
                        {deleting ? (
                            <><FontAwesomeIcon icon={faSpinner} spin /> Deleting...</>
                        ) : (
                            <><FontAwesomeIcon icon={faTrash} /> Delete Transaction</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Manual Dues Addition Modal Component
// ==============================================
const CaManualDuesModal = ({ show, supplier, onClose, onSave, translations, showToast, currentShopId, onRefresh }) => {
    const [amount, setAmount] = useState('');
    const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);
    const [referenceNo, setReferenceNo] = useState('');
    const [description, setDescription] = useState('');
    const [notes, setNotes] = useState('');
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const { user } = useAuth();

    const shopId = currentShopId || localStorage.getItem('current_shop_id') || '';

    useEffect(() => {
        if (show && supplier) {
            setAmount('');
            setDueDate(new Date().toISOString().split('T')[0]);
            setReferenceNo('');
            setDescription('');
            setNotes('');
            setErrors({});
        }
    }, [show, supplier]);

    const validateForm = () => {
        const newErrors = {};

        if (!amount || parseFloat(amount) <= 0) {
            newErrors.amount = translations?.validAmountRequired || 'Please enter a valid amount';
        }

        if (!dueDate) {
            newErrors.due_date = translations?.dueDateRequired || 'Due date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        if (saving) return;
        setSaving(true);

        try {
            const username = user?.username || localStorage.getItem('username');
            const transactionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            const payload = {
                action: 'add_po_manual_balance',
                supplier_id: supplier.supplierid,
                shop_id: shopId,
                amount: parseFloat(amount),
                balance_date: dueDate,
                username: username,
                notes: notes,
                reference_no: referenceNo,
                description: description || 'Manual dues added for previous purchases',
                idempotency_key: transactionId
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/po_supplier_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                if (showToast) {
                    showToast('success', data.message || translations?.manualDuesSuccess || 'Manual dues added successfully');
                }
                if (onSave) {
                    await onSave(data.data);
                }
                if (onRefresh) {
                    onRefresh();
                }
                onClose();
            } else {
                if (showToast) {
                    showToast('error', data.message || translations?.manualDuesFailed || 'Failed to add manual dues');
                } else {
                    alert(data.message || translations?.manualDuesFailed || 'Failed to add manual dues');
                }
            }
        } catch (error) {
            console.error('Manual dues error:', error);
            if (showToast) {
                showToast('error', translations?.networkError || 'Network error occurred');
            } else {
                alert(translations?.networkError || 'Network error occurred');
            }
        } finally {
            setSaving(false);
        }
    };

    const isRTL = (localStorage.getItem('ourzest_language') || 'en') === 'ur' || (localStorage.getItem('ourzest_language') || 'en') === 'ar';

    if (!show) return null;

    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-manual-balance-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faPlusCircle} /> {translations?.manualDues || 'Add Manual Dues'}</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="ca-modal-body">
                    {supplier && (
                        <div className="ca-customer-info-summary">
                            <div className="ca-customer-name-large">
                                <FontAwesomeIcon icon={faUser} />
                                {supplier.supplier_name}
                            </div>
                            {supplier.phone && (
                                <div className="ca-customer-phone-large">
                                    <FontAwesomeIcon icon={faPhone} />
                                    {supplier.phone}
                                </div>
                            )}
                            {supplier.company && (
                                <div className="ca-customer-company-large">
                                    <FontAwesomeIcon icon={faBuilding} />
                                    {supplier.company}
                                </div>
                            )}
                            <div className="ca-current-balance">
                                <span>{translations?.currentBalance || 'Current Balance'}: </span>
                                <span className={parseFloat(supplier.po_current_balance) > 0 ? 'positive' : ''}>
                                    {formatCurrency(supplier.po_current_balance || 0)}
                                </span>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="ca-form-section">
                            <h3 className="ca-section-title"><FontAwesomeIcon icon={faFileInvoice} /> {translations?.balanceDetails || 'Dues Details'}</h3>

                            <div className="ca-form-grid">
                                <div className={`ca-form-group ${errors.amount ? 'has-error' : ''}`}>
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faRupeeSign} className="ca-input-icon" />
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            placeholder=" "
                                            disabled={saving}
                                            dir="ltr"
                                        />
                                        <label className="ca-input-label">{translations?.amount || 'Amount'} <span className="ca-required">*</span></label>
                                    </div>
                                    {errors.amount && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.amount}</div>}
                                    <div className="ca-help-text">
                                        {translations?.manualDuesHelp || 'Enter the previous due amount to add to supplier\'s account'}
                                    </div>
                                </div>

                                <div className={`ca-form-group ${errors.due_date ? 'has-error' : ''}`}>
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="ca-input-icon" />
                                        <input
                                            type="date"
                                            value={dueDate}
                                            onChange={(e) => setDueDate(e.target.value)}
                                            disabled={saving}
                                            dir="ltr"
                                        />
                                        <label className="ca-input-label">{translations?.dueDate || 'Due Date'} <span className="ca-required">*</span></label>
                                    </div>
                                    {errors.due_date && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.due_date}</div>}
                                </div>

                                <div className="ca-form-group">
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faBarcode} className="ca-input-icon" />
                                        <input
                                            type="text"
                                            value={referenceNo}
                                            onChange={(e) => setReferenceNo(e.target.value)}
                                            placeholder=" "
                                            disabled={saving}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <label className="ca-input-label">{translations?.referenceNumber || 'Reference Number'}</label>
                                    </div>
                                </div>
                            </div>

                            <div className="ca-form-group ca-full-width">
                                <div className="ca-input-container">
                                    <FontAwesomeIcon icon={faFileInvoice} className="ca-input-icon" />
                                    <input
                                        type="text"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder=" "
                                        disabled={saving}
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label className="ca-input-label">{translations?.description || 'Description'}</label>
                                </div>
                            </div>

                            <div className="ca-form-group ca-full-width">
                                <div className="ca-input-container ca-textarea-container">
                                    <FontAwesomeIcon icon={faReceipt} className="ca-input-icon" />
                                    <textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder=" "
                                        disabled={saving}
                                        rows="3"
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    ></textarea>
                                    <label className="ca-input-label">{translations?.notes || 'Notes (e.g., Previous purchases, Old dues, etc.)'}</label>
                                </div>
                            </div>
                        </div>

                        {amount && parseFloat(amount) > 0 && supplier && (
                            <div className="ca-balance-summary">
                                <h4>{translations?.balanceSummary || 'Balance Summary'}</h4>
                                <div className="ca-summary-row">
                                    <span>{translations?.currentBalance || 'Current Balance'}:</span>
                                    <span>{formatCurrency(supplier.po_current_balance || 0)}</span>
                                </div>
                                <div className="ca-summary-row ca-success">
                                    <span>{translations?.amountToAdd || 'Amount to Add'}:</span>
                                    <span>+ {formatCurrency(amount)}</span>
                                </div>
                                <div className="ca-summary-row ca-border-top ca-bold">
                                    <span>{translations?.newBalance || 'New Balance After Addition'}:</span>
                                    <span>{formatCurrency((supplier.po_current_balance || 0) + parseFloat(amount))}</span>
                                </div>
                            </div>
                        )}

                        <div className="ca-modal-footer">
                            <button type="button" className="ca-btn-secondary" onClick={onClose}>
                                {translations?.cancel || 'Cancel'}
                            </button>
                            <button type="submit" className="ca-btn-primary" disabled={saving}>
                                {saving ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> {translations?.adding || 'Adding...'}</>
                                ) : (
                                    <><FontAwesomeIcon icon={faSave} /> {translations?.addDues || 'Add Dues'}</>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Advanced Filters Component
// ==============================================
const CaSupplierFilters = ({ filters, onFilterChange, onClearFilters, onApplyFilters, showFilters, setShowFilters, translations, paymentMethods }) => {
    const [localFilters, setLocalFilters] = useState(filters);

    const handleLocalChange = (field, value) => {
        setLocalFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleApply = () => {
        onFilterChange(localFilters);
        onApplyFilters();
    };

    const handleClear = () => {
        const clearedFilters = {
            transaction_type: 'all',
            source: 'all',
            payment_method: 'all',
            date_from: '',
            date_to: '',
            search: ''
        };
        setLocalFilters(clearedFilters);
        onFilterChange(clearedFilters);
        onClearFilters();
    };

    if (!showFilters) return null;

    return (
        <div className="ca-filters-panel">
            <div className="ca-filters-header">
                <h4><FontAwesomeIcon icon={faFilter} /> {translations.advancedFilters || 'Advanced Filters'}</h4>
                <button className="ca-filters-close" onClick={() => setShowFilters(false)}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>

            <div className="ca-filters-body">
                <div className="ca-filter-section">
                    <label className="ca-filter-label">{translations.transactionType || 'Transaction Type'}</label>
                    <div className="ca-filter-options">
                        <button
                            className={`ca-filter-option ${localFilters.transaction_type === 'all' ? 'active' : ''}`}
                            onClick={() => handleLocalChange('transaction_type', 'all')}
                        >
                            All
                        </button>
                        <button
                            className={`ca-filter-option ${localFilters.transaction_type === 'debit' ? 'active' : ''}`}
                            onClick={() => handleLocalChange('transaction_type', 'debit')}
                        >
                            Payments
                        </button>
                        <button
                            className={`ca-filter-option ${localFilters.transaction_type === 'credit' ? 'active' : ''}`}
                            onClick={() => handleLocalChange('transaction_type', 'credit')}
                        >
                            Credits
                        </button>
                    </div>
                </div>

                <div className="ca-filter-section">
                    <label className="ca-filter-label">Source</label>
                    <div className="ca-filter-options">
                        <button
                            className={`ca-filter-option ${localFilters.source === 'all' ? 'active' : ''}`}
                            onClick={() => handleLocalChange('source', 'all')}
                        >
                            All
                        </button>
                        <button
                            className={`ca-filter-option ${localFilters.source === 'purchase' ? 'active' : ''}`}
                            onClick={() => handleLocalChange('source', 'purchase')}
                        >
                            Purchase Orders
                        </button>
                        <button
                            className={`ca-filter-option ${localFilters.source === 'manual' ? 'active' : ''}`}
                            onClick={() => handleLocalChange('source', 'manual')}
                        >
                            Manual Entry
                        </button>
                    </div>
                </div>

                <div className="ca-filter-section">
                    <label className="ca-filter-label">{translations.paymentMethod || 'Payment Method'}</label>
                    <div className="ca-filter-options">
                        <button
                            className={`ca-filter-option ${localFilters.payment_method === 'all' ? 'active' : ''}`}
                            onClick={() => handleLocalChange('payment_method', 'all')}
                        >
                            All
                        </button>
                        {paymentMethods.map(method => (
                            <button
                                key={method.value}
                                className={`ca-filter-option ${localFilters.payment_method === method.value ? 'active' : ''}`}
                                onClick={() => handleLocalChange('payment_method', method.value)}
                            >
                                {method.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="ca-filter-section">
                    <label className="ca-filter-label">Date Range</label>
                    <div className="ca-date-range-inputs">
                        <div className="ca-date-input-group">
                            <FontAwesomeIcon icon={faCalendarAlt} />
                            <input
                                type="date"
                                value={localFilters.date_from || ''}
                                onChange={(e) => handleLocalChange('date_from', e.target.value)}
                                placeholder="From Date"
                            />
                        </div>
                        <span className="ca-date-separator">—</span>
                        <div className="ca-date-input-group">
                            <FontAwesomeIcon icon={faCalendarAlt} />
                            <input
                                type="date"
                                value={localFilters.date_to || ''}
                                onChange={(e) => handleLocalChange('date_to', e.target.value)}
                                placeholder="To Date"
                            />
                        </div>
                    </div>
                </div>

                <div className="ca-filter-section">
                    <label className="ca-filter-label">{translations.search || 'Search'}</label>
                    <div className="ca-search-input-group">
                        <FontAwesomeIcon icon={faSearch} />
                        <input
                            type="text"
                            placeholder="Search by reference, description..."
                            value={localFilters.search || ''}
                            onChange={(e) => handleLocalChange('search', e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="ca-filters-footer">
                <button className="ca-btn-clear-filters" onClick={handleClear}>
                    <FontAwesomeIcon icon={faTimes} /> {translations.clearFilters || 'Clear Filters'}
                </button>
                <button className="ca-btn-apply-filters" onClick={handleApply}>
                    <FontAwesomeIcon icon={faCheckCircle} /> {translations.applyFilters || 'Apply Filters'}
                </button>
            </div>
        </div>
    );
};

// ==============================================
// Main PO Supplier Transaction Component
// ==============================================
const POSupplierTransaction = () => {
    const { user, currentShop } = useAuth();

    const [shopId, setShopId] = useState(() => {
        if (currentShop?.shop_id) return currentShop.shop_id;
        if (user?.shop_id) return user.shop_id;
        return localStorage.getItem('current_shop_id') || '';
    });

    // State
    const [suppliersList, setSuppliersList] = useState([]);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState({ url: null, name: null });
    const [showFilters, setShowFilters] = useState(false);
    const [activeTab, setActiveTab] = useState('form');
    const [showManualDuesModal, setShowManualDuesModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Sort configuration
    const [sortConfig, setSortConfig] = useState({
        key: 'transaction_date',
        direction: 'desc'
    });

    // Transaction form state
    const [transactionType, setTransactionType] = useState('debit');
    const [amount, setAmount] = useState('');
    const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
    const [referenceNo, setReferenceNo] = useState('');
    const [description, setDescription] = useState('');
    const [notes, setNotes] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [receiptFile, setReceiptFile] = useState(null);
    const [receiptPreview, setReceiptPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const fileInputRef = useRef(null);

    // Transactions history
    const [transactions, setTransactions] = useState([]);
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    const [stats, setStats] = useState({ total_debit: 0, total_credit: 0, total_transactions: 0 });

    // Filters
    const [filters, setFilters] = useState({
        transaction_type: 'all',
        source: 'all',
        payment_method: 'all',
        date_from: '',
        date_to: '',
        search: ''
    });

    const currentLanguage = localStorage.getItem('ourzest_language') || 'en';
    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // Translations
    const translations = {
        supplierTransactions: 'PO Supplier Transactions',
        manageTransactions: 'Manage supplier payments and returns with receipt uploads',
        suppliers: 'Suppliers',
        searchSuppliers: 'Search suppliers by name or phone...',
        noSuppliersFound: 'No suppliers found',
        selectSupplier: 'Select a Supplier',
        selectSupplierMessage: 'Select a supplier from the list to view their account and manage transactions',
        newTransaction: 'New Transaction',
        transactionHistory: 'Transaction History',
        supplierName: 'Supplier Name',
        outstandingBalance: 'Outstanding Balance',
        totalPaid: 'Total Paid',
        totalPurchases: 'Total Purchases',
        transactionType: 'Transaction Type',
        paymentToSupplier: 'Payment to Supplier',
        returnFromSupplier: 'Return / Credit from Supplier',
        paymentHint: '💵 You are paying money TO the supplier (decreases what you owe)',
        returnHint: '📦 Supplier is giving you credit/return (decreases what you owe)',
        amount: 'Amount',
        transactionDate: 'Transaction Date',
        referenceNo: 'Reference Number',
        paymentMethod: 'Payment Method',
        description: 'Description',
        notes: 'Notes',
        receiptAttachment: 'Receipt / Attachment',
        uploadReceipt: 'Click to upload receipt',
        uploadHint: 'JPG, PNG, GIF, PDF (Max 10MB)',
        clear: 'Clear',
        recordPayment: 'Record Payment',
        recordReturn: 'Record Return',
        processing: 'Processing...',
        transactionNo: 'Transaction No',
        date: 'Date',
        type: 'Type',
        source: 'Source',
        payment: 'Payment',
        purchase: 'Purchase',
        return: 'Return',
        reference: 'Reference',
        receipt: 'Receipt',
        actions: 'Actions',
        viewDetails: 'View Details',
        viewReceipt: 'View Receipt',
        noTransactions: 'No transaction records found',
        loading: 'Loading...',
        youOwe: 'You owe this amount',
        paid: 'Paid',
        purchaseOrder: 'Purchase Order',
        manualEntry: 'Manual Entry',
        cash: 'Cash',
        bankTransfer: 'Bank Transfer',
        digitalWallet: 'Digital Wallet',
        cheque: 'Cheque',
        card: 'Card',
        upi: 'UPI',
        success: 'Success',
        error: 'Error',
        transactionRecorded: 'Transaction recorded successfully',
        transactionFailed: 'Failed to record transaction',
        networkError: 'Network error occurred',
        validAmount: 'Please enter a valid amount',
        selectSupplierRequired: 'Please select a supplier',
        filter: 'Filter',
        advancedFilters: 'Advanced Filters',
        clearFilters: 'Clear Filters',
        applyFilters: 'Apply Filters',
        manualDues: 'Add Manual Dues',
        manualDuesSuccess: 'Manual dues added successfully',
        manualDuesFailed: 'Failed to add manual dues',
        manualDuesHelp: 'Enter the previous due amount to add to supplier\'s account',
        dueDate: 'Due Date',
        dueDateRequired: 'Due date is required',
        addDues: 'Add Dues',
        amountToAdd: 'Amount to Add',
        newBalance: 'New Balance After Addition',
        balanceSummary: 'Balance Summary',
        balanceDetails: 'Dues Details',
        adding: 'Adding...',
        validAmountRequired: 'Please enter a valid amount',
        currentBalance: 'Current Balance',
        referenceNumber: 'Reference Number',
        cancel: 'Cancel'
    };

    const showToast = (type, message) => {
        setToast({ show: true, type, message });
        setTimeout(() => setToast({ show: false }), 3000);
    };

    // Load suppliers list
    const loadSuppliers = useCallback(async () => {
        if (!shopId) return;
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/po_supplier_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_all_suppliers_with_po_balance',
                    shop_id: parseInt(shopId),
                    username: user?.username,
                    search: searchTerm
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setSuppliersList(data.data || []);
            }
        } catch (error) {
            console.error('Error loading suppliers:', error);
            showToast('error', 'Failed to load suppliers');
        } finally {
            setLoading(false);
        }
    }, [shopId, user?.username, searchTerm]);

    // Load transactions for selected supplier
    const loadTransactions = useCallback(async () => {
        if (!shopId || !selectedSupplier) return;
        setLoadingTransactions(true);
        try {
            const requestData = {
                action: 'get_supplier_transactions',
                supplier_id: parseInt(selectedSupplier.supplierid),
                shop_id: parseInt(shopId),
                username: user?.username,
                limit: 200
            };

            if (filters.transaction_type && filters.transaction_type !== 'all') {
                requestData.transaction_type = filters.transaction_type;
            }
            if (filters.source && filters.source !== 'all') {
                requestData.source = filters.source;
            }
            if (filters.payment_method && filters.payment_method !== 'all') {
                requestData.payment_method = filters.payment_method;
            }
            if (filters.date_from) {
                requestData.date_from = filters.date_from;
            }
            if (filters.date_to) {
                requestData.date_to = filters.date_to;
            }

            const response = await fetch('https://ourzestapp.online/oz_backend/po_supplier_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });
            const data = await response.json();
            if (data.status === 'success') {
                setTransactions(data.data || []);
            }
        } catch (error) {
            console.error('Error loading transactions:', error);
            showToast('error', 'Failed to load transactions');
        } finally {
            setLoadingTransactions(false);
        }
    }, [shopId, selectedSupplier, user?.username, filters]);

    // Load stats
    const loadStats = useCallback(async () => {
        if (!shopId) return;
        try {
            const requestData = {
                action: 'get_transaction_stats',
                shop_id: parseInt(shopId)
            };
            if (selectedSupplier) {
                requestData.supplier_id = parseInt(selectedSupplier.supplierid);
            }

            const response = await fetch('https://ourzestapp.online/oz_backend/po_supplier_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });
            const data = await response.json();
            if (data.status === 'success') {
                setStats(data.data);
            }
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    }, [shopId, selectedSupplier]);

    useEffect(() => {
        if (shopId && user?.username) {
            loadSuppliers();
            loadStats();
        }
    }, [shopId, user?.username, loadSuppliers, loadStats]);

    useEffect(() => {
        if (selectedSupplier) {
            loadTransactions();
            loadStats();
        }
    }, [selectedSupplier, loadTransactions, loadStats]);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (shopId) {
                loadSuppliers();
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm, shopId, loadSuppliers]);

    // Apply theme colors
    useEffect(() => {
        const themeColor = localStorage.getItem('ourzest_theme') || '#6366f1';
        const root = document.documentElement;
        const hex = themeColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        const adjust = (amount) => {
            let newR = Math.max(0, Math.min(255, r + amount));
            let newG = Math.max(0, Math.min(255, g + amount));
            let newB = Math.max(0, Math.min(255, b + amount));
            return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
        };

        root.style.setProperty('--ca-primary', themeColor);
        root.style.setProperty('--ca-primary-dark', adjust(-40));
        root.style.setProperty('--ca-primary-light', adjust(40));
        root.style.setProperty('--ca-primary-ultralight', adjust(20));
        root.style.setProperty('--ca-primary-gradient', `linear-gradient(135deg, ${themeColor}, ${adjust(-20)})`);
        root.style.setProperty('--ca-primary-shadow', `${themeColor}20`);
    }, []);

    const validateForm = () => {
        const newErrors = {};
        if (!selectedSupplier) newErrors.supplier = translations.selectSupplierRequired;
        if (!amount || parseFloat(amount) <= 0) newErrors.amount = translations.validAmount;
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const resetForm = () => {
        setAmount('');
        setReferenceNo('');
        setDescription('');
        setNotes('');
        setPaymentMethod('cash');
        setReceiptFile(null);
        setReceiptPreview(null);
        setErrors({});
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                showToast('error', 'File size must be less than 10MB');
                return;
            }
            setReceiptFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setReceiptPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveReceipt = () => {
        setReceiptFile(null);
        setReceiptPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setSaving(true);
        try {
            const username = user?.username || localStorage.getItem('username');

            const formData = new FormData();
            formData.append('action', 'record_transaction');
            formData.append('supplier_id', selectedSupplier.supplierid);
            formData.append('shop_id', parseInt(shopId));
            formData.append('amount', parseFloat(amount));
            formData.append('transaction_type', transactionType === 'debit' ? 'debit' : 'credit');
            formData.append('source', 'manual');
            formData.append('transaction_date', transactionDate);
            formData.append('username', username);
            formData.append('payment_method', paymentMethod);
            formData.append('reference_no', referenceNo);
            formData.append('description', description);
            formData.append('notes', notes);

            if (receiptFile) {
                formData.append('receipt', receiptFile);
            }

            const response = await fetch('https://ourzestapp.online/oz_backend/po_supplier_transactions.php', {
                method: 'POST',
                body: formData
            });
            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', data.message || translations.transactionRecorded);
                resetForm();
                loadSuppliers();
                loadTransactions();
                loadStats();
                setActiveTab('transactions');
            } else {
                showToast('error', data.message || translations.transactionFailed);
            }
        } catch (error) {
            console.error('Error:', error);
            showToast('error', translations.networkError);
        } finally {
            setSaving(false);
        }
    };

    const handleViewTransaction = (transaction) => {
        setSelectedTransaction(transaction);
        setShowDetailsModal(true);
    };

    const handleViewReceipt = (receiptUrl, receiptName) => {
        setSelectedReceipt({ url: receiptUrl, name: receiptName });
        setShowReceiptModal(true);
    };

    const handleManualDuesSubmit = async (duesData) => {
        if (!selectedSupplier || !shopId) return;
        await loadSuppliers();
        await loadTransactions();
        await loadStats();
    };

    const refreshAfterManualDues = async () => {
        if (selectedSupplier) {
            await loadSuppliers();
            await loadTransactions();
            await loadStats();
        }
    };

    const refreshAfterEditDelete = async () => {
        if (selectedSupplier) {
            await loadSuppliers();
            await loadTransactions();
            await loadStats();
        }
    };

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };

    const handleApplyFilters = () => {
        loadTransactions();
        setShowFilters(false);
    };

    const handleClearFilters = () => {
        setFilters({
            transaction_type: 'all',
            source: 'all',
            payment_method: 'all',
            date_from: '',
            date_to: '',
            search: ''
        });
        setTimeout(() => loadTransactions(), 100);
    };

    // Handle sort
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Get sorted data
    const getSortedData = (data) => {
        if (!data || !data.length) return [];

        const sorted = [...data];
        sorted.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (sortConfig.key === 'transaction_date') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            } else if (sortConfig.key === 'amount') {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            } else {
                aVal = (aVal || '').toString().toLowerCase();
                bVal = (bVal || '').toString().toLowerCase();
            }

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    };

    const filteredSuppliers = suppliersList;
    const sortedTransactions = getSortedData(transactions);

    const paymentMethodsList = [
        { value: 'cash', label: translations.cash, icon: faMoneyBillWave },
        { value: 'bank_transfer', label: translations.bankTransfer, icon: faLandmark },
        { value: 'digital_wallet', label: translations.digitalWallet, icon: faWallet },
        { value: 'cheque', label: translations.cheque, icon: faReceipt },
        { value: 'card', label: translations.card, icon: faCreditCard },
        { value: 'upi', label: translations.upi, icon: faExchangeAlt }
    ];

    // Function to get badge class for transaction
    const getTransactionBadgeClass = (tx) => {
        if (tx.transaction_type === 'debit') {
            return 'ca-debit';
        } else {
            if (tx.source === 'purchase') {
                return 'ca-purchase';
            } else {
                return 'ca-credit';
            }
        }
    };

    const getTransactionDisplayType = (tx) => {
        if (tx.transaction_type === 'debit') {
            return 'Payment';
        } else {
            if (tx.source === 'purchase') {
                return 'Purchase';
            } else {
                return 'Return';
            }
        }
    };

    return (
        <div className={`ca-container ${isRTL ? 'ca-rtl' : ''}`}>
            <CaToastNotification toast={toast} setToast={setToast} translations={translations} />

            <CaReceiptPreviewModal
                show={showReceiptModal}
                onClose={() => setShowReceiptModal(false)}
                receiptUrl={selectedReceipt.url}
                receiptName={selectedReceipt.name}
            />

            <CaTransactionDetailsModal
                show={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                transaction={selectedTransaction}
                onViewReceipt={handleViewReceipt}
            />

            <CaManualDuesModal
                show={showManualDuesModal}
                supplier={selectedSupplier}
                onClose={() => setShowManualDuesModal(false)}
                onSave={handleManualDuesSubmit}
                onRefresh={refreshAfterManualDues}
                translations={translations}
                showToast={showToast}
                currentShopId={shopId}
            />

            <CaEditSupplierTransactionModal
                show={showEditModal}
                transaction={selectedTransaction}
                supplier={selectedSupplier}
                onClose={() => setShowEditModal(false)}
                onSave={refreshAfterEditDelete}
                showToast={showToast}
                currentShopId={shopId}
            />

            <CaDeleteSupplierTransactionModal
                show={showDeleteModal}
                transaction={selectedTransaction}
                supplier={selectedSupplier}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={refreshAfterEditDelete}
                showToast={showToast}
                currentShopId={shopId}
            />

            {/* Header */}
            <div className="ca-header">
                <div className="ca-header-content">
                    <div className="ca-header-icon">
                        <FontAwesomeIcon icon={faBuilding} />
                    </div>
                    <div className="ca-header-text">
                        <h1>{translations.supplierTransactions}</h1>
                        <p>{translations.manageTransactions}</p>
                        {currentShop && (
                            <div className="ca-shop-badge">
                                <FontAwesomeIcon icon={faTag} /> {currentShop.shop_name}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Layout */}
            <div className="ca-main-layout">
                {/* Left Sidebar - Suppliers List */}
                <div className={`ca-sidebar ${sidebarCollapsed ? 'ca-collapsed' : ''}`}>
                    <div className="ca-sidebar-header">
                        <button className="ca-sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                            <FontAwesomeIcon icon={sidebarCollapsed ? faChevronRight : faChevronLeft} />
                        </button>
                        {!sidebarCollapsed && (
                            <div className="ca-sidebar-title">
                                <FontAwesomeIcon icon={faUsers} />
                                <span>{translations.suppliers}</span>
                            </div>
                        )}
                    </div>

                    {!sidebarCollapsed && (
                        <>
                            <div className="ca-sidebar-search">
                                <div className="ca-search-wrapper">
                                    <FontAwesomeIcon icon={faSearch} className="ca-search-icon" />
                                    <input
                                        type="text"
                                        placeholder={translations.searchSuppliers}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {loading && <FontAwesomeIcon icon={faSpinner} spin className="ca-search-spinner" />}
                                </div>
                            </div>

                            <div className="ca-customer-list">
                                {loading ? (
                                    <div className="ca-loading-customers">
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        <span>{translations.loading}</span>
                                    </div>
                                ) : filteredSuppliers.length === 0 ? (
                                    <div className="ca-no-customers">
                                        <FontAwesomeIcon icon={faBuilding} />
                                        <span>{translations.noSuppliersFound}</span>
                                    </div>
                                ) : (
                                    filteredSuppliers.map(supplier => (
                                        <div
                                            key={supplier.supplierid}
                                            className={`ca-customer-item ${selectedSupplier?.supplierid === supplier.supplierid ? 'ca-active' : ''}`}
                                            onClick={() => setSelectedSupplier(supplier)}
                                        >
                                            <div className="ca-customer-content">
                                                <div className="ca-customer-name">
                                                    <FontAwesomeIcon icon={faUser} className="ca-item-icon" />
                                                    <span className="ca-customer-name-text">{supplier.supplier_name}</span>
                                                </div>
                                                {supplier.company && (
                                                    <div className="ca-customer-phone">
                                                        <FontAwesomeIcon icon={faBuilding} size="xs" /> {supplier.company}
                                                    </div>
                                                )}
                                                {supplier.phone && (
                                                    <div className="ca-customer-phone">
                                                        <FontAwesomeIcon icon={faPhone} size="xs" /> {supplier.phone}
                                                    </div>
                                                )}
                                                <div className={`ca-customer-balance ${parseFloat(supplier.po_current_balance) > 0 ? 'ca-positive' : ''}`}>
                                                    {formatCurrency(supplier.po_current_balance)}
                                                    {parseFloat(supplier.po_current_balance) > 0 && (
                                                        <small className="ca-balance-hint">{translations.youOwe}</small>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Main Content */}
                <div className="ca-main-content">
                    {selectedSupplier ? (
                        <div className="ca-account-content">
                            {/* Summary Cards */}
                            <div className="ca-summary-cards">
                                <div className="ca-summary-card">
                                    <div className="ca-card-icon"><FontAwesomeIcon icon={faUser} /></div>
                                    <div className="ca-card-content">
                                        <div className="ca-card-label">{translations.supplierName}</div>
                                        <div className="ca-card-value">{selectedSupplier.supplier_name}</div>
                                        {selectedSupplier.company && (
                                            <div className="ca-card-subvalue">{selectedSupplier.company}</div>
                                        )}
                                        {selectedSupplier.phone && (
                                            <div className="ca-card-subvalue"><FontAwesomeIcon icon={faPhone} size="xs" /> {selectedSupplier.phone}</div>
                                        )}
                                    </div>
                                </div>

                                <div className={`ca-summary-card ca-balance-card ${parseFloat(selectedSupplier.po_current_balance) > 0 ? 'ca-balance-positive' : ''}`}>
                                    <div className="ca-card-icon"><FontAwesomeIcon icon={faBalanceScale} /></div>
                                    <div className="ca-card-content">
                                        <div className="ca-card-label">{translations.outstandingBalance}</div>
                                        <div className="ca-card-value">{formatCurrency(selectedSupplier.po_current_balance)}</div>
                                        <div className="ca-card-subvalue">
                                            <span className="ca-debit-stat">{translations.totalPaid}: {formatCurrency(selectedSupplier.po_total_debit)}</span> | 
                                            <span className="ca-credit-stat">{translations.totalPurchases}: {formatCurrency(selectedSupplier.po_total_credit)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="ca-summary-card">
                                    <div className="ca-card-icon"><FontAwesomeIcon icon={faMoneyBill} /></div>
                                    <div className="ca-card-content">
                                        <div className="ca-card-label">{translations.totalPaid}</div>
                                        <div className="ca-card-value">{formatCurrency(selectedSupplier.po_total_debit)}</div>
                                        <div className="ca-card-subvalue">Total Payments</div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="ca-action-buttons">
                                <button className={`ca-btn-secondary ${activeTab === 'form' ? 'ca-active' : ''}`} onClick={() => setActiveTab('form')}>
                                    <FontAwesomeIcon icon={faMoneyBillWave} /> {translations.newTransaction}
                                </button>
                                <button className={`ca-btn-secondary ${activeTab === 'transactions' ? 'ca-active' : ''}`} onClick={() => setActiveTab('transactions')}>
                                    <FontAwesomeIcon icon={faHistory} /> {translations.transactionHistory}
                                </button>
                                <button
                                    className={`ca-btn-filter ${showFilters ? 'ca-active' : ''}`}
                                    onClick={() => setShowFilters(!showFilters)}
                                >
                                    <FontAwesomeIcon icon={faFilter} /> {translations.filter}
                                </button>
                                <button 
                                    className="ca-btn-manual"
                                    onClick={() => setShowManualDuesModal(true)}
                                >
                                    <FontAwesomeIcon icon={faPlusCircle} /> {translations.manualDues || 'Add Manual Dues'}
                                </button>
                            </div>

                            {/* Advanced Filters Panel */}
                            <CaSupplierFilters
                                filters={filters}
                                onFilterChange={handleFilterChange}
                                onClearFilters={handleClearFilters}
                                onApplyFilters={handleApplyFilters}
                                showFilters={showFilters}
                                setShowFilters={setShowFilters}
                                translations={translations}
                                paymentMethods={paymentMethodsList}
                            />

                            {/* Filter Summary */}
                            {(filters.transaction_type !== 'all' || filters.source !== 'all' || filters.payment_method !== 'all' || filters.date_from || filters.date_to || filters.search) && (
                                <div className="ca-filter-summary">
                                    <span className="ca-filter-summary-label">Active Filters:</span>
                                    {filters.transaction_type !== 'all' && <span className="ca-filter-tag">Type: {filters.transaction_type === 'debit' ? 'Payments' : 'Credits'}</span>}
                                    {filters.source !== 'all' && <span className="ca-filter-tag">Source: {filters.source === 'purchase' ? 'Purchase Orders' : 'Manual Entry'}</span>}
                                    {filters.payment_method !== 'all' && <span className="ca-filter-tag">Method: {filters.payment_method}</span>}
                                    {filters.date_from && <span className="ca-filter-tag">From: {filters.date_from}</span>}
                                    {filters.date_to && <span className="ca-filter-tag">To: {filters.date_to}</span>}
                                    {filters.search && <span className="ca-filter-tag">Search: "{filters.search}"</span>}
                                    <button className="ca-clear-filters" onClick={handleClearFilters}>
                                        <FontAwesomeIcon icon={faTimes} /> Clear All
                                    </button>
                                </div>
                            )}

                            {/* Form Tab */}
                            {activeTab === 'form' && (
                                <form onSubmit={handleSubmit} className="ca-transaction-form">
                                    {/* Transaction Type Toggle */}
                                    <div className="ca-transaction-type-toggle">
                                        <label className="ca-filter-label">{translations.transactionType}:</label>
                                        <div className="ca-toggle-buttons">
                                            <button
                                                type="button"
                                                className={`ca-type-btn ca-debit-btn ${transactionType === 'debit' ? 'ca-active' : ''}`}
                                                onClick={() => setTransactionType('debit')}
                                            >
                                                <FontAwesomeIcon icon={faArrowDown} /> {translations.paymentToSupplier}
                                            </button>
                                            <button
                                                type="button"
                                                className={`ca-type-btn ca-credit-btn ${transactionType === 'credit' ? 'ca-active' : ''}`}
                                                onClick={() => setTransactionType('credit')}
                                            >
                                                <FontAwesomeIcon icon={faArrowUp} /> {translations.returnFromSupplier}
                                            </button>
                                        </div>
                                        <small className="ca-type-hint">
                                            {transactionType === 'debit' ? translations.paymentHint : translations.returnHint}
                                        </small>
                                    </div>

                                    {/* Form Grid - 2 columns */}
                                    <div className="ca-form-grid">
                                        {/* Amount Field */}
                                        <div className={`ca-form-group ${errors.amount ? 'has-error' : ''}`}>
                                            <div className="ca-input-container">
                                                <FontAwesomeIcon icon={faRupeeSign} className="ca-input-icon" />
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={amount}
                                                    onChange={(e) => setAmount(e.target.value)}
                                                    placeholder=" "
                                                    disabled={saving}
                                                    dir="ltr"
                                                />
                                                <label className="ca-input-label">{translations.amount} <span className="ca-required">*</span></label>
                                            </div>
                                            {errors.amount && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.amount}</div>}
                                        </div>

                                        {/* Date Field */}
                                        <div className="ca-form-group">
                                            <div className="ca-input-container">
                                                <FontAwesomeIcon icon={faCalendarAlt} className="ca-input-icon" />
                                                <input
                                                    type="date"
                                                    value={transactionDate}
                                                    onChange={(e) => setTransactionDate(e.target.value)}
                                                    placeholder=" "
                                                    disabled={saving}
                                                    dir="ltr"
                                                />
                                                <label className="ca-input-label">{translations.transactionDate}</label>
                                            </div>
                                        </div>

                                        {/* Reference No Field */}
                                        <div className="ca-form-group">
                                            <div className="ca-input-container">
                                                <FontAwesomeIcon icon={faBarcode} className="ca-input-icon" />
                                                <input
                                                    type="text"
                                                    value={referenceNo}
                                                    onChange={(e) => setReferenceNo(e.target.value)}
                                                    placeholder=" "
                                                    disabled={saving}
                                                    dir={isRTL ? 'rtl' : 'ltr'}
                                                />
                                                <label className="ca-input-label">{translations.referenceNo}</label>
                                            </div>
                                        </div>

                                        {/* Payment Method */}
                                        <div className="ca-form-group">
                                            <div className="ca-input-container">
                                                <FontAwesomeIcon icon={faCreditCard} className="ca-input-icon" />
                                                <select
                                                    value={paymentMethod}
                                                    onChange={(e) => setPaymentMethod(e.target.value)}
                                                    disabled={saving}
                                                    className="ca-form-select"
                                                >
                                                    {paymentMethodsList.map(method => (
                                                        <option key={method.value} value={method.value}>
                                                            {method.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <label className="ca-input-label">{translations.paymentMethod}</label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Full width fields */}
                                    <div className="ca-form-group ca-full-width">
                                        <div className="ca-input-container">
                                            <FontAwesomeIcon icon={faFileInvoice} className="ca-input-icon" />
                                            <input
                                                type="text"
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                placeholder=" "
                                                disabled={saving}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            />
                                            <label className="ca-input-label">{translations.description}</label>
                                        </div>
                                    </div>

                                    <div className="ca-form-group ca-full-width">
                                        <div className="ca-input-container ca-textarea-container">
                                            <FontAwesomeIcon icon={faReceipt} className="ca-input-icon" />
                                            <textarea
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                                placeholder=" "
                                                disabled={saving}
                                                rows="3"
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            ></textarea>
                                            <label className="ca-input-label">{translations.notes}</label>
                                        </div>
                                    </div>

                                    {/* Receipt Upload */}
                                    <div className="ca-form-group ca-full-width">
                                        <div className="ca-receipt-upload-area">
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                onChange={handleFileChange}
                                                accept="image/*,application/pdf"
                                                className="ca-receipt-input"
                                                id="ca-receipt-upload"
                                            />
                                            <label htmlFor="ca-receipt-upload" className="ca-receipt-upload-label">
                                                <FontAwesomeIcon icon={faUpload} />
                                                <span>{translations.uploadReceipt}</span>
                                                <small>{translations.uploadHint}</small>
                                            </label>

                                            {receiptPreview && (
                                                <div className="ca-receipt-preview">
                                                    <img src={receiptPreview} alt="Receipt preview" />
                                                    <button type="button" className="ca-remove-receipt" onClick={handleRemoveReceipt}>
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Form Actions */}
                                    <div className="ca-form-actions">
                                        <button type="button" className="ca-btn-secondary" onClick={resetForm}>
                                            <FontAwesomeIcon icon={faTimes} /> {translations.clear}
                                        </button>
                                        <button type="submit" className={`ca-btn-primary ${transactionType === 'debit' ? 'ca-debit' : 'ca-credit'}`} disabled={saving}>
                                            {saving ? (
                                                <><FontAwesomeIcon icon={faSpinner} spin /> {translations.processing}</>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={transactionType === 'debit' ? faArrowDown : faArrowUp} />
                                                    {transactionType === 'debit' ? ` ${translations.recordPayment}` : ` ${translations.recordReturn}`}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* Transactions Tab */}
                            {activeTab === 'transactions' && (
                                <>
                                    {loadingTransactions ? (
                                        <div className="ca-loading-state"><FontAwesomeIcon icon={faSpinner} spin /> {translations.loading}</div>
                                    ) : transactions.length === 0 ? (
                                        <div className="ca-empty-state">
                                            <FontAwesomeIcon icon={faHistory} />
                                            <p>{translations.noTransactions}</p>
                                        </div>
                                    ) : (
                                        <div className="ca-table-container">
                                            <table className="ca-table">
                                                <thead>
                                                    <tr>
                                                        <CaSortableHeader column="transaction_no" label={translations.transactionNo} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                                        <CaSortableHeader column="transaction_date" label={translations.date} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                                        <th>{translations.type}</th>
                                                        <th>{translations.source}</th>
                                                        <CaSortableHeader column="amount" label={translations.amount} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                                        <th>{translations.paymentMethod}</th>
                                                        <th>{translations.reference}</th>
                                                        <th>{translations.receipt}</th>
                                                        <th>{translations.actions}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sortedTransactions.map(tx => (
                                                        <tr key={tx.id}>
                                                            <td className="ca-tx-no">{tx.transaction_no}</td>
                                                            <td>{formatDate(tx.transaction_date)}</td>
                                                                                                                        <td className="ca-tx-no">{tx.transaction_no}</td>
                                                            <td>{formatDate(tx.transaction_date)}</td>
                                                            <td>
                                                                <span className={`ca-transaction-type ${getTransactionBadgeClass(tx)}`}>
                                                                    {getTransactionDisplayType(tx)}
                                                                </span>
                                                                {tx.transaction_type === 'credit' && tx.source === 'purchase' && (
                                                                    <small className="ca-type-hint-small">({translations.youOwe})</small>
                                                                )}
                                                                {tx.transaction_type === 'debit' && (
                                                                    <small className="ca-type-hint-small">({translations.paid})</small>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <span className="ca-source-badge">
                                                                    {tx.source === 'purchase' ? translations.purchaseOrder : translations.manualEntry}
                                                                </span>
                                                            </td>
                                                            <td className={`ca-text-right ${tx.transaction_type === 'debit' ? 'ca-debit' : 'ca-credit'}`}>
                                                                {tx.transaction_type === 'debit' ? '-' : '+'}{tx.amount ? formatCurrency(tx.amount) : formatCurrency(0)}
                                                            </td>
                                                            <td>
                                                                <span className="ca-payment-method">
                                                                    {tx.payment_method?.replace('_', ' ') || translations.cash}
                                                                </span>
                                                            </td>
                                                            <td>{tx.reference_no || '-'}</td>
                                                            <td>
                                                                {tx.receipt_image ? (
                                                                    <button
                                                                        className="ca-btn-icon ca-receipt-btn"
                                                                        onClick={() => handleViewReceipt(tx.receipt_image, tx.receipt_original_name)}
                                                                        title={translations.viewReceipt}
                                                                    >
                                                                        <FontAwesomeIcon icon={faFileImage} />
                                                                    </button>
                                                                ) : (
                                                                    <span className="ca-no-receipt">-</span>
                                                                )}
                                                            </td>
                                                            <td className="ca-actions-cell">
                                                                <button
                                                                    className="ca-btn-icon ca-view-btn"
                                                                    onClick={() => handleViewTransaction(tx)}
                                                                    title={translations.viewDetails}
                                                                >
                                                                    <FontAwesomeIcon icon={faEye} />
                                                                </button>
                                                                <button
                                                                    className="ca-btn-icon ca-edit-btn"
                                                                    onClick={() => {
                                                                        setSelectedTransaction(tx);
                                                                        setShowEditModal(true);
                                                                    }}
                                                                    title="Edit Transaction"
                                                                >
                                                                    <FontAwesomeIcon icon={faEdit} />
                                                                </button>
                                                                <button
                                                                    className="ca-btn-icon ca-delete-btn"
                                                                    onClick={() => {
                                                                        setSelectedTransaction(tx);
                                                                        setShowDeleteModal(true);
                                                                    }}
                                                                    title="Delete Transaction"
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="ca-no-selected">
                            <FontAwesomeIcon icon={faBuilding} className="ca-empty-icon" />
                            <h3>{translations.selectSupplier}</h3>
                            <p>{translations.selectSupplierMessage}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Loading Overlay */}
            {loading && (
                <div className="ca-loading-overlay">
                    <div className="ca-loading-content">
                        <div className="ca-loading-spinner"></div>
                        <p>{translations.loading}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default POSupplierTransaction;